import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Ship, ArrowRight, Container, MapPin, Clock, Search, ChevronRight, CheckCircle, 
  BarChart3, FileText, Users, Zap, Lock, Navigation, Globe, Truck, Anchor, 
  Bell, MessageSquare, Shield, TrendingUp, Package, Plane, Train, Phone, Mail, 
  MapPinned, Facebook, Linkedin, Instagram, Sparkles, Eye, ExternalLink, X, Building, User,
  Smartphone, Box, ChevronDown, Loader2, Music2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { RoketPublicWidget } from "../components/RoketPublicWidget";
import { RoketPromoTrack } from "../components/RoketPromoTrack";

// Lazy load the map component
const ShippingRouteMap = lazy(() => import("../components/ShippingRouteMap"));

// myEuropacific logo
const MYEUROPACIFIC_LOGO = "https://customer-assets.emergentagent.com/job_65c103ce-390f-4bae-b6d5-2798d8bc6281/artifacts/6in60tne_image.png";
// Original Europacific logo for company section
const EUROPACIFIC_LOGO = "https://europacific.si/wp-content/uploads/2019/03/53379708_385493638849149_6528926021696094208_n-1.png";
const ROKET_IMAGE = "https://customer-assets.emergentagent.com/job_6e94cf61-ed07-442f-8a8f-1e142db37be8/artifacts/bvtijyq3_IMG_6616.jpg";

// Stock images for "From Blind Spots to Full Visibility" section
const PORT_IMG = "https://images.pexels.com/photos/1624695/pexels-photo-1624695.jpeg?auto=compress&w=600";
const SHIP_IMG = "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=600&q=80";
const CRANE_IMG = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80";
const TRUCK_IMG = "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&w=600";

// Different images for "About Europacific" section
const ABOUT_IMG_1 = "https://images.unsplash.com/photo-1769144256181-698b8f807066?w=600&q=80"; // Cargo ship with containers
const ABOUT_IMG_2 = "https://images.pexels.com/photos/5257005/pexels-photo-5257005.jpeg?auto=compress&w=600"; // Team meeting
const ABOUT_IMG_3 = "https://images.pexels.com/photos/7519251/pexels-photo-7519251.jpeg?auto=compress&w=600"; // Aerial containers
const ABOUT_IMG_4 = "https://images.pexels.com/photos/3840441/pexels-photo-3840441.jpeg?auto=compress&w=600"; // Port aerial

const TOP_CARRIERS = ["Maersk", "MSC", "CMA CGM", "COSCO", "Hapag-Lloyd", "ONE", "Evergreen", "Yang Ming", "HMM", "ZIM"];

// Port options for lookup
const PORTS = [
  { id: "koper", name: "Luka Koper", country: "Slovenia", icon: "🇸🇮" },
  { id: "agct", name: "AGCT Rijeka", country: "Croatia", icon: "🇭🇷" },
  { id: "apm", name: "APM Rijeka", country: "Croatia", icon: "🇭🇷" }
];

export default function LandingPage() {
  const navigate = useNavigate();
  
  // Carrier tracking state
  const [containerNumber, setContainerNumber] = useState("");
  const [isTracking, setIsTracking] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [hasUsedFreeTracking] = useState(localStorage.getItem("guest_tracking_used") === "true");
  
  // Port lookup state
  const [selectedPort, setSelectedPort] = useState("");
  const [portContainer, setPortContainer] = useState("");
  const [isPortLookup, setIsPortLookup] = useState(false);
  const [portResult, setPortResult] = useState(null);

  // 2026-05-16 — AWB tracking (FREE, no auth) for the air-freight customers
  const [awbInput, setAwbInput] = useState("");
  const [isAwbLookup, setIsAwbLookup] = useState(false);
  const [awbResult, setAwbResult] = useState(null);

  // Quote modal state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    estimated_lookups_per_year: "", notes: ""
  });
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  // Carrier tracking handler
  const handleGuestTracking = async () => {
    if (!containerNumber || containerNumber.length < 10) {
      toast.error("Enter a valid container number (e.g., MSKU1234567)");
      return;
    }
    if (hasUsedFreeTracking) {
      navigate("/register?reason=tracking");
      return;
    }
    setIsTracking(true);
    try {
      const response = await axios.post(`${API_URL}/tracking/guest`, { container_number: containerNumber });
      const data = response.data;
      localStorage.setItem("guest_tracking_used", "true");
      setTrackingResult({
        container: data.container_number,
        status: data.status || "Unknown",
        location: data.location || "Unknown",
        vessel: data.vessel || "Unknown",
        carrier: data.carrier || "Unknown",
        eta: data.eta,
        progress: data.progress || 50,
        found: data.found
      });
      toast.success(data.found ? "Container found!" : "Lookup completed.");
    } catch (error) {
      toast.error("Tracking service temporarily unavailable.");
      setTrackingResult(null);
    }
    setIsTracking(false);
  };

  // Port lookup handler - FREE, no limits
  const handlePortLookup = async () => {
    if (!selectedPort) {
      toast.error("Please select a port");
      return;
    }
    if (!portContainer || portContainer.length < 10) {
      toast.error("Enter a valid container number");
      return;
    }
    setIsPortLookup(true);
    setPortResult(null);
    try {
      const response = await axios.post(`${API_URL}/port-lookup`, {
        port: selectedPort,
        container_number: portContainer.toUpperCase()
      });
      setPortResult(response.data);
      if (response.data.found) {
        toast.success(`Container found at ${PORTS.find(p => p.id === selectedPort)?.name}`);
      } else {
        toast.info("Container not found at this port");
      }
    } catch (error) {
      toast.error("Port lookup failed. Please try again.");
      setPortResult({ found: false, error: true });
    }
    setIsPortLookup(false);
  };

  // 2026-05-16 — AWB lookup handler (free, no auth, no rate-limit)
  const handleAwbLookup = async () => {
    const cleaned = (awbInput || "").trim();
    if (cleaned.replace(/\D/g, "").length < 11) {
      toast.error("AWB must contain at least 11 digits (3 prefix + 8 serial)");
      return;
    }
    setIsAwbLookup(true);
    setAwbResult(null);
    try {
      const r = await axios.get(`${API_URL}/public/awb-tracking/${encodeURIComponent(cleaned)}`);
      setAwbResult(r.data);
      if (r.data?.found) {
        toast.success(`Airline detected: ${r.data.airline}`);
      } else {
        toast.info(r.data?.reason || "Airline prefix not recognised");
      }
    } catch (e) {
      toast.error("AWB lookup failed. Please try again.");
      setAwbResult(null);
    }
    setIsAwbLookup(false);
  };

  // Quote submission handler
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.company_name || !quoteForm.contact_name || !quoteForm.email) {
      toast.error("Please fill in required fields");
      return;
    }
    if (!quoteForm.estimated_lookups_per_year || parseInt(quoteForm.estimated_lookups_per_year) < 1) {
      toast.error("Please specify expected unique searches per year");
      return;
    }
    setIsSubmittingQuote(true);
    try {
      const response = await axios.post(`${API_URL}/tracking/credits/request`, {
        ...quoteForm, estimated_lookups_per_year: parseInt(quoteForm.estimated_lookups_per_year)
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowQuoteModal(false);
        setQuoteForm({ company_name: "", contact_name: "", email: "", phone: "", estimated_lookups_per_year: "", notes: "" });
      }
    } catch (error) {
      toast.error("Failed to submit. Please email us directly.");
    }
    setIsSubmittingQuote(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <RoketPublicWidget />
      {/* Quote Modal */}
      <AnimatePresence>
        {showQuoteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowQuoteModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full overflow-hidden border-2 border-amber-400">
                    <img src={ROKET_IMAGE} alt="Roket" className="w-full h-full object-cover" style={{ objectPosition: 'center 20%' }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Request Tracking Credits</h3>
                    <p className="text-xs text-gray-500">We'll prepare a custom yearly offer</p>
                  </div>
                </div>
                <button onClick={() => setShowQuoteModal(false)} className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleQuoteSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Company *</label>
                    <Input value={quoteForm.company_name} onChange={(e) => setQuoteForm({...quoteForm, company_name: e.target.value})} 
                      placeholder="Your company" className="h-10" required />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Contact *</label>
                    <Input value={quoteForm.contact_name} onChange={(e) => setQuoteForm({...quoteForm, contact_name: e.target.value})} 
                      placeholder="Your name" className="h-10" required />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Email *</label>
                  <Input type="email" value={quoteForm.email} onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})} 
                    placeholder="email@company.com" className="h-10" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
                    <Input value={quoteForm.phone} onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})} 
                      placeholder="+386..." className="h-10" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block flex items-center gap-1">
                      Searches/Year * <span className="text-amber-500 text-[10px]">(required)</span>
                    </label>
                    <Input type="number" value={quoteForm.estimated_lookups_per_year} 
                      onChange={(e) => setQuoteForm({...quoteForm, estimated_lookups_per_year: e.target.value})} 
                      placeholder="e.g. 600" className="h-10 border-amber-200" min="1" required />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                  <textarea value={quoteForm.notes} onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})} 
                    placeholder="Any specific requirements..." className="w-full border rounded-lg px-3 py-2 text-sm h-16 resize-none" />
                </div>
                <Button type="submit" disabled={isSubmittingQuote} className="w-full h-11 bg-[#003366] hover:bg-[#002244] text-white">
                  {isSubmittingQuote ? "Submitting..." : "Request Quote"}{!isSubmittingQuote && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={MYEUROPACIFIC_LOGO} alt="myEuropacific - Logistics simplified" className="h-11" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#services" className="text-gray-600 hover:text-[#003366]">Services</a>
            <a href="#tracking" className="text-gray-600 hover:text-[#003366]">Tracking</a>
            <a href="#about" className="text-gray-600 hover:text-[#003366]">About</a>
            <Link to="/carrier-registration" className="text-gray-600 hover:text-[#003366]" data-testid="nav-carrier-registration">Carriers</Link>
            <Link to="/customer-registration" className="text-gray-600 hover:text-[#003366]" data-testid="nav-customer-registration">Register</Link>
            <a href="#contact" className="text-gray-600 hover:text-[#003366]">Contact</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/customer-registration" data-testid="header-customer-registration"><Button variant="outline" className="hidden sm:inline-flex h-9 text-sm border-[#003366] text-[#003366] hover:bg-[#f0f7ff] px-4">Register as Customer</Button></Link>
            <Link to="/carrier-registration" data-testid="header-carrier-registration"><Button variant="outline" className="hidden sm:inline-flex h-9 text-sm border-[#003366] text-[#003366] hover:bg-[#f0f7ff] px-4">Become a Carrier</Button></Link>
            <Link to="/login"><Button variant="ghost" className="text-gray-700 h-9 text-sm">Sign In</Button></Link>
            <Link to="/register"><Button className="h-9 text-sm bg-[#003366] hover:bg-[#002244] text-white px-5">Get Started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 bg-gradient-to-b from-[#f0f7ff] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="text-center mb-6">
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 px-4 py-2 rounded-full mb-3">
              <motion.img 
                src={ROKET_IMAGE} 
                alt="Roket" 
                className="h-8 w-8 rounded-full object-cover border-2 border-amber-300"
                style={{ objectPosition: 'center 20%' }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-amber-700 font-semibold text-sm">Meet Roket - Your Tracking Companion</span>
            </motion.div>
            
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              Complete Logistics Visibility
              <br /><span className="text-[#003366]">From Origin to Destination</span>
            </motion.h1>
            
            {/* Feature Pills */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="flex flex-wrap justify-center gap-2 mb-2">
              {[
                { icon: Container, label: "FCL Tracking", color: "text-blue-500" },
                { icon: Box, label: "LCL BL Tracking", color: "text-purple-500" },
                { icon: Truck, label: "Live Truck GPS", color: "text-emerald-500" },
                { icon: Anchor, label: "Port Lookups", color: "text-[#003366]" },
                { icon: Bell, label: "Email/SMS/WhatsApp", color: "text-amber-500" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100">
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                  <span className="text-gray-700 text-xs font-medium">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Main Grid: Tracking Tools + Map */}
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Left Column: Tracking Tools */}
            <div className="space-y-4">
              {/* Carrier Tracking */}
              <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Ship className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Carrier Tracking</h3>
                    <p className="text-[10px] text-gray-500">100+ ocean carriers worldwide</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input value={containerNumber} onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
                    placeholder="Container / BL number" className="h-10 text-sm font-mono" data-testid="tracking-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleGuestTracking()} />
                  <Button onClick={handleGuestTracking} disabled={isTracking} data-testid="track-button"
                    className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white text-sm">
                    {isTracking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Track"}
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {TOP_CARRIERS.slice(0, 4).map(c => (
                    <span key={c} className="text-[9px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">{c}</span>
                  ))}
                  <span className="text-[9px] text-gray-400">+96 more</span>
                </div>
                {hasUsedFreeTracking && (
                  <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1">
                    <Lock className="h-3 w-3" /><Link to="/register" className="underline">Register for more carrier lookups</Link>
                  </p>
                )}
              </div>

              {/* Port Lookup - FREE */}
              <div className="bg-white rounded-xl p-4 shadow-lg border border-emerald-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                    <Anchor className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Port Lookup</h3>
                    <p className="text-[10px] text-emerald-600 font-medium">FREE - No registration required</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Select value={selectedPort} onValueChange={setSelectedPort}>
                    <SelectTrigger className="h-10 text-sm" data-testid="port-select">
                      <SelectValue placeholder="Select port..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PORTS.map(port => (
                        <SelectItem key={port.id} value={port.id}>
                          <span className="flex items-center gap-2">
                            <span>{port.icon}</span>
                            <span>{port.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Input value={portContainer} onChange={(e) => setPortContainer(e.target.value.toUpperCase())}
                      placeholder="Container number" className="h-10 text-sm font-mono" data-testid="port-container-input"
                      onKeyPress={(e) => e.key === 'Enter' && handlePortLookup()} />
                    <Button onClick={handlePortLookup} disabled={isPortLookup} data-testid="port-lookup-button"
                      className="h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm">
                      {isPortLookup ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
                    </Button>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  Koper, AGCT Rijeka & APM Rijeka terminals
                </p>
              </div>

              {/* 2026-05-16 — Air Cargo (AWB) Tracking — FREE */}
              <div className="bg-white rounded-xl p-4 shadow-lg border border-sky-200" data-testid="awb-tracking-card">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-sky-500 flex items-center justify-center">
                    <Plane className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Air Waybill (AWB) Tracking</h3>
                    <p className="text-[10px] text-sky-600 font-medium">FREE · 150+ airlines worldwide</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={awbInput}
                    onChange={(e) => setAwbInput(e.target.value)}
                    placeholder="e.g. 176-12345678"
                    className="h-10 text-sm font-mono"
                    data-testid="awb-input"
                    onKeyPress={(e) => e.key === "Enter" && handleAwbLookup()}
                  />
                  <Button
                    onClick={handleAwbLookup}
                    disabled={isAwbLookup}
                    data-testid="awb-track-button"
                    className="h-10 px-4 bg-sky-500 hover:bg-sky-600 text-white text-sm"
                  >
                    {isAwbLookup ? <Loader2 className="h-4 w-4 animate-spin" /> : "Track"}
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  First 3 digits identify the airline (e.g. 176 = Emirates, 235 = Thai)
                </p>
              </div>

              {/* 2026-05-16 — AWB Result */}
              {awbResult && (
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`rounded-xl p-4 border ${awbResult.found ? "bg-sky-50 border-sky-200" : "bg-amber-50 border-amber-200"}`}
                  data-testid="awb-result"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {awbResult.found ? (
                      <CheckCircle className="h-5 w-5 text-sky-600" />
                    ) : (
                      <X className="h-5 w-5 text-amber-500" />
                    )}
                    <span className="font-semibold text-sm">
                      {awbResult.found ? awbResult.airline : "Airline prefix not recognised"}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="text-gray-500">AWB:</span>{" "}
                      <span className="font-mono font-medium">{awbResult.mawb_full || awbResult.input}</span>
                    </p>
                    {awbResult.iata_code && (
                      <p>
                        <span className="text-gray-500">IATA Code:</span>{" "}
                        <span className="font-medium">{awbResult.iata_code}</span>{" "}
                        <span className="text-gray-400 text-xs">· prefix {awbResult.prefix}</span>
                      </p>
                    )}
                  </div>
                  {awbResult.tracking_url && (
                    <a
                      href={awbResult.tracking_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex w-full items-center justify-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium py-2 rounded-lg"
                      data-testid="awb-open-tracking"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {awbResult.found
                        ? `Open ${awbResult.iata_code} tracking page`
                        : "Search Google for this AWB"}
                    </a>
                  )}
                </motion.div>
              )}

              {/* Port Result */}
              {portResult && (
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  className={`rounded-xl p-4 border ${portResult.found ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                  data-testid="port-result">
                  <div className="flex items-center gap-2 mb-2">
                    {portResult.found ? (
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <X className="h-5 w-5 text-amber-500" />
                    )}
                    <span className="font-semibold text-sm">
                      {portResult.found ? "Container Found" : "Not Found"}
                    </span>
                  </div>
                  {portResult.found && (
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Status:</span> <span className="font-medium">{portResult.status}</span></p>
                      {portResult.location && <p><span className="text-gray-500">Location:</span> <span className="font-medium">{portResult.location}</span></p>}
                      {portResult.arrival_date && <p><span className="text-gray-500">Arrived:</span> <span className="font-medium">{portResult.arrival_date}</span></p>}
                      {portResult.departure_date && <p><span className="text-gray-500">Departed:</span> <span className="font-medium">{portResult.departure_date}</span></p>}
                      {portResult.transport_mode && <p><span className="text-gray-500">Via:</span> <span className="font-medium">{portResult.transport_mode}</span></p>}
                    </div>
                  )}
                  {!portResult.found && (
                    <p className="text-sm text-amber-700">Container not found at selected port. Try another port or check the number.</p>
                  )}
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <p className="text-[10px] text-gray-500">
                      Want alerts? <Link to="/register" className="text-[#003366] font-medium underline">Register to set up watchlist</Link>
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Carrier Tracking Result */}
              {trackingResult && (
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100" data-testid="tracking-result">
                  <div className={`p-3 text-white ${trackingResult.found ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-sm font-bold">{trackingResult.container}</span>
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{trackingResult.status}</span>
                    </div>
                  </div>
                  <div className="p-3 text-xs">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div><span className="text-gray-500">Location</span><p className="font-semibold">{trackingResult.location}</p></div>
                      <div><span className="text-gray-500">Carrier</span><p className="font-semibold">{trackingResult.carrier}</p></div>
                    </div>
                    <div className="mb-2">
                      <div className="flex justify-between text-gray-500 mb-0.5"><span>Progress</span><span>{trackingResult.progress}%</span></div>
                      <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${trackingResult.progress}%` }} /></div>
                    </div>
                    <Link to="/register">
                      <Button size="sm" className="w-full h-8 bg-[#003366] text-white text-xs">
                        <Bell className="h-3 w-3 mr-1" />Get Alerts & Watchlist
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right: World Map */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-full">
                {/* Map Header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-[#003366]" />
                    <span className="font-semibold text-gray-800 text-sm">Live Route Tracking</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[#003366] font-medium">Shanghai, CN</span>
                    <ArrowRight className="h-3 w-3 text-gray-400" />
                    <span className="text-emerald-600 font-medium">Koper, SI</span>
                  </div>
                </div>

                {/* Interactive Leaflet Map */}
                <Suspense fallback={
                  <div className="flex items-center justify-center" style={{ height: '340px' }}>
                    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                }>
                  <ShippingRouteMap 
                    route="shanghai_koper" 
                    height="340px" 
                    showLabels={true} 
                    animateShip={true}
                  />
                </Suspense>

                {/* Timeline */}
                <div className="px-4 py-3 border-t border-gray-100 flex items-center">
                  <div className="text-center pr-4 border-r border-gray-200">
                    <p className="text-[10px] text-gray-500">ATD Shanghai</p>
                    <p className="text-xs font-bold text-[#003366]">2 Jan 2026</p>
                  </div>
                  <div className="flex-1 px-4">
                    <div className="relative h-2 bg-gray-100 rounded-full">
                      <motion.div 
                        className="absolute h-full bg-gradient-to-r from-[#003366] to-emerald-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 2 }}
                      />
                      <div className="absolute top-1/2 left-[20%] -translate-y-1/2 w-2 h-2 bg-[#00a0dc] rounded-full border border-white" title="Singapore" />
                      <div className="absolute top-1/2 left-[40%] -translate-y-1/2 w-2 h-2 bg-[#00a0dc] rounded-full border border-white" title="Colombo" />
                      <div className="absolute top-1/2 left-[70%] -translate-y-1/2 w-2 h-2 bg-[#00a0dc] rounded-full border border-white" title="Suez" />
                    </div>
                  </div>
                  <div className="text-center pl-4 border-l border-gray-200">
                    <p className="text-[10px] text-gray-500">ATA Koper</p>
                    <p className="text-xs font-bold text-emerald-600">17 Feb 2026</p>
                  </div>
                </div>

                {/* Port Badges */}
                <div className="px-4 py-2 bg-gray-50 flex items-center justify-center gap-3 text-[11px]">
                  <div className="flex items-center gap-1">
                    <span>🇸🇮</span><span className="text-gray-700 font-medium">Luka Koper</span>
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🇭🇷</span><span className="text-gray-700 font-medium">AGCT Rijeka</span>
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🇭🇷</span><span className="text-gray-700 font-medium">APM Rijeka</span>
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features Summary */}
          <div className="mt-6 bg-gradient-to-r from-[#003366] to-[#004488] rounded-xl p-4 text-white">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              {[
                { icon: Container, text: "FCL - 100+ carriers" },
                { icon: Box, text: "LCL & HBL tracking" },
                { icon: Truck, text: "Live truck GPS" },
                { icon: Train, text: "Rail tracking" },
                { icon: Anchor, text: "Port terminals" },
                { icon: Bell, text: "Email/SMS/WhatsApp" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <item.icon className="h-4 w-4 text-amber-300" />
                  <span className="text-blue-100 text-xs">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Track & Trace + Roket on WhatsApp */}
      <RoketPromoTrack />

      {/* Platform Features */}
      <section className="py-12 bg-white" id="services">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Logistics Platform</h2>
            <p className="text-gray-600 text-sm">Everything you need to manage shipments, customers, and operations.</p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { title: "FCL Tracking", desc: "100+ ocean carriers", icon: Container, color: "bg-blue-500" },
              { title: "LCL & HBL", desc: "NVOCCs & coloaders", icon: Box, color: "bg-purple-500" },
              { title: "Live Truck GPS", desc: "In-house developed", icon: Truck, color: "bg-emerald-500" },
              { title: "Rail Tracking", desc: "European rail network", icon: Train, color: "bg-amber-500" },
              { title: "Position Folders", desc: "Full documentation", icon: FileText, color: "bg-red-500" },
              { title: "Finance Module", desc: "A/R & SOA automation", icon: BarChart3, color: "bg-indigo-500" },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-lg transition-shadow text-center">
                <div className={`h-12 w-12 ${item.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Integration Section */}
      <section className="py-12 bg-gradient-to-br from-slate-900 to-slate-800" id="api">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 px-3 py-1 rounded-full mb-4">
                <Zap className="h-4 w-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">API Integration</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Connect Your Systems<br />
                <span className="text-emerald-400">Seamlessly</span>
              </h2>
              <p className="text-slate-300 text-sm mb-4">
                Our open API allows freight forwarders, NVOCCs, and logistics partners to push shipment data 
                directly into myEuropacific. Real-time updates, automatic matching, and zero manual entry.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  "Push new shipments via REST API",
                  "Real-time status updates (loaded, departed, ETA)",
                  "Automatic HBL matching to Position Folders",
                  "Schedule updates per Bill of Lading",
                  "Secure API key authentication",
                  "Comprehensive documentation"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/docs/lcl-status">
                <Button className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white text-sm">
                  View API Documentation<ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <span className="text-slate-400 text-xs font-mono">POST /api/lcl-status/shipments</span>
              </div>
              <pre className="text-xs text-slate-300 font-mono overflow-x-auto">
{`{
  "hbl_number": "HBL2024001",
  "origin_port": "CNSHA",
  "destination_port": "SIKOP",
  "shipper": {
    "name": "Shanghai Electronics"
  },
  "consignee": {
    "name": "EuroPacific Slovenia"
  },
  "cargo": {
    "description": "Electronic Parts"
  },
  "eta": "2026-04-05T00:00:00Z"
}`}
              </pre>
              <div className="mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <p className="text-emerald-400 text-xs font-mono">
                  ✓ Shipment linked to PF-000123-26
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracking Section */}
      <section className="py-12 bg-gray-50" id="tracking">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full mb-4">
                <img src={ROKET_IMAGE} alt="Roket" className="h-6 w-6 rounded-full object-cover border border-amber-300" style={{ objectPosition: 'center 20%' }} />
                <span className="text-amber-700 text-sm font-medium">Roket's Story</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">From Blind Spots to<br /><span className="text-[#003366]">Full Visibility</span></h2>
              <p className="text-gray-600 text-sm mb-4">
                Our customers were always in the dark - calling agents, waiting for emails. 
                Now with <span className="text-amber-600 font-semibold">Roket</span> and our tracking systems, 
                you see everything in real-time.
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  "FCL tracking across 100+ ocean carriers",
                  "LCL shipments & House BL tracking",
                  "In-house developed live truck GPS",
                  "Port of Koper & Rijeka terminals",
                  "Email, SMS & WhatsApp notifications",
                  "Container watchlist with auto-alerts"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className="h-10 bg-[#003366] hover:bg-[#002244] text-white text-sm">
                  Start Tracking Free<ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img src={SHIP_IMG} alt="Ship" className="rounded-xl h-32 w-full object-cover shadow" />
              <img src={TRUCK_IMG} alt="Truck" className="rounded-xl h-32 w-full object-cover shadow" />
              <img src={CRANE_IMG} alt="Port" className="rounded-xl h-32 w-full object-cover shadow" />
              <img src={PORT_IMG} alt="Terminal" className="rounded-xl h-32 w-full object-cover shadow" />
            </div>
          </div>
        </div>
      </section>

      {/* Credits CTA */}
      <section className="py-10 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src={ROKET_IMAGE} alt="Roket" className="h-8 w-8 rounded-full object-cover border-2 border-amber-300" style={{ objectPosition: 'center 20%' }} />
            <span className="text-amber-300 font-semibold text-sm">Need More Tracking?</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Tracking Credits for External Users</h2>
          <p className="text-blue-100 text-sm mb-4 max-w-xl mx-auto">
            Not a Europacific customer? Get flexible tracking credit packages for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><Button className="bg-white text-[#003366] hover:bg-gray-100 h-10 px-5 text-sm">Create Free Account</Button></Link>
            <Button onClick={() => setShowQuoteModal(true)} variant="outline" 
              className="border-white/30 text-white hover:bg-white/10 h-10 px-5 text-sm bg-transparent" data-testid="request-quote-btn">
              <Mail className="mr-2 h-4 w-4" />Request Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { stat: "100+", label: "Ocean Carriers", icon: Ship },
              { stat: "20K+", label: "Shipments/Year", icon: Container },
              { stat: "600+", label: "Containers/Month", icon: Package },
              { stat: "1400+", label: "Active Customers", icon: Users },
            ].map((item) => (
              <div key={item.label}>
                <div className="h-10 w-10 rounded-full bg-[#003366]/10 flex items-center justify-center mx-auto mb-2">
                  <item.icon className="h-5 w-5 text-[#003366]" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{item.stat}</p>
                <p className="text-gray-500 text-xs">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-12 bg-gray-50" id="about">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">About Europacific</h2>
              <p className="text-gray-600 text-sm mb-3">
                Europacific is a modern international logistics company organizing land, sea and air transport with highly qualified staff.
              </p>
              <p className="text-gray-600 text-sm mb-3">
                We have recognized cooperation with <span className="text-[#003366] font-semibold">Port of Koper</span>, 
                successful relationships with Slovenian railway undertakings, and reputable shipping agencies.
              </p>
              <p className="text-gray-600 text-sm mb-4">
                Active in Slovenia, Romania, Croatia, Bosnia, Serbia, Montenegro, Macedonia, Austria, Hungary, Ukraine, Czech Republic, Slovakia.
              </p>
              <a href="https://europacific.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white text-sm">
                  Visit Corporate Site<ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img src={ABOUT_IMG_1} alt="Port Operations" className="rounded-xl h-32 w-full object-cover shadow" />
              <img src={ABOUT_IMG_2} alt="Warehouse Team" className="rounded-xl h-32 w-full object-cover shadow" />
              <img src={ABOUT_IMG_3} alt="Piran Slovenia" className="rounded-xl h-32 w-full object-cover shadow" />
              <img src={ABOUT_IMG_4} alt="Koper Sunset" className="rounded-xl h-32 w-full object-cover shadow" />
            </div>
          </div>
        </div>
      </section>

      {/* Get the App Section */}
      <section className="py-12 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left - App Preview */}
              <div className="bg-gradient-to-br from-[#003366] to-[#004488] p-8 flex flex-col items-center justify-center text-white">
                <div className="relative">
                  <div className="w-48 h-80 bg-slate-900 rounded-3xl border-4 border-slate-700 shadow-2xl overflow-hidden">
                    <div className="h-6 bg-slate-800 flex items-center justify-center">
                      <div className="w-16 h-3 bg-slate-700 rounded-full" />
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="bg-blue-500/30 rounded-lg p-2 text-center">
                        <p className="text-xs font-bold">myEuropacific</p>
                        <p className="text-[10px] opacity-70">Track anywhere</p>
                      </div>
                      <div className="space-y-1">
                        <div className="h-8 bg-white/10 rounded flex items-center px-2 gap-2">
                          <Ship className="h-3 w-3" />
                          <span className="text-[9px]">My Shipments</span>
                        </div>
                        <div className="h-8 bg-white/10 rounded flex items-center px-2 gap-2">
                          <Search className="h-3 w-3" />
                          <span className="text-[9px]">Track Container</span>
                        </div>
                        <div className="h-8 bg-white/10 rounded flex items-center px-2 gap-2">
                          <MessageSquare className="h-3 w-3" />
                          <span className="text-[9px]">Messages</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <motion.div 
                    className="absolute -bottom-2 -right-2 bg-amber-400 rounded-full p-2"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Smartphone className="h-5 w-5 text-slate-900" />
                  </motion.div>
                </div>
              </div>

              {/* Right - Install Instructions */}
              <div className="p-8">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone className="h-5 w-5 text-[#003366]" />
                  <span className="text-xs font-medium text-[#003366] uppercase tracking-wide">Mobile App</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Get myEuropacific App</h2>
                <p className="text-gray-600 text-sm mb-6">
                  Track shipments, message your dispatcher, and pay invoices - all from your phone.
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#003366]">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Open in your browser</p>
                      <p className="text-xs text-gray-500">Visit this page on your phone</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#003366]">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Add to Home Screen</p>
                      <p className="text-xs text-gray-500">Tap the share icon, then "Add to Home Screen"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Done! Open anytime</p>
                      <p className="text-xs text-gray-500">Works offline, just like a native app</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/mobile" className="flex-1">
                    <Button className="w-full bg-[#003366] text-white hover:bg-[#004488] h-11">
                      <Smartphone className="h-4 w-4 mr-2" />
                      Open Mobile App
                    </Button>
                  </Link>
                  <Link to="/register" className="flex-1">
                    <Button variant="outline" className="w-full h-11 border-[#003366] text-[#003366]">
                      <User className="h-4 w-4 mr-2" />
                      Create Account
                    </Button>
                  </Link>
                </div>

                <p className="text-xs text-gray-400 mt-4 text-center">
                  No app store download needed. Works on iOS & Android.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-gradient-to-r from-[#003366] to-[#004488]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <motion.div 
              className="h-12 w-12 rounded-full overflow-hidden border-3 border-amber-400 shadow-lg"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img src={ROKET_IMAGE} alt="Roket" className="w-full h-full object-cover" style={{ objectPosition: 'center 20%' }} />
            </motion.div>
            <span className="text-amber-300 font-bold">Roket says:</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-3">"Stop waiting. Start tracking!"</h2>
          <p className="text-blue-100 text-sm mb-4">Join 1,400+ companies who trust Europacific for logistics visibility.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><Button className="bg-white text-[#003366] hover:bg-gray-100 h-10 px-6 text-sm">Create Free Account<ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/login"><Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-10 px-6 text-sm bg-transparent">Sign In to Portal</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-900 text-white" id="contact">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6 mb-6">
            <div>
              {/* Original Europacific Logo for company info */}
              <img src={EUROPACIFIC_LOGO} alt="Europacific" className="h-10 mb-3 brightness-200" />
              <p className="text-gray-400 text-xs">International transport, forwarding & logistics</p>
              <p className="text-gray-500 text-xs mt-2">Since 2003</p>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-3">Contact</h4>
              <div className="space-y-2 text-xs text-gray-400">
                <p className="flex items-start gap-2"><MapPinned className="h-3 w-3 text-[#00a0dc] mt-0.5" />Ankaranska cesta 5, 6000 Koper, Slovenia</p>
                <p className="flex items-center gap-2"><Phone className="h-3 w-3 text-[#00a0dc]" />+386 5 6626 380</p>
                <p className="flex items-center gap-2"><Mail className="h-3 w-3 text-[#00a0dc]" />info@europacific.com</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-3">Services</h4>
              <ul className="space-y-1 text-xs text-gray-400">
                <li>Sea Freight (FCL/LCL)</li><li>Road Transport</li><li>Rail Transport</li><li>Air Freight</li><li>Customs Clearance</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-3">Quick Links</h4>
              <ul className="space-y-1 text-xs text-gray-400">
                <li><Link to="/login" className="hover:text-white">myEuropacific Portal</Link></li>
                <li><Link to="/mobile" className="hover:text-white flex items-center gap-1"><Smartphone className="h-3 w-3" />Get the App</Link></li>
                <li><a href="https://europacific.com" className="hover:text-white">Corporate Site</a></li>
                <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms & Conditions</Link></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-gray-800 gap-3">
            <div className="flex items-center gap-3">
              <a href="https://www.instagram.com/europacifickp" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#003366]" title="Instagram"><Instagram className="h-4 w-4" /></a>
              <a href="https://www.facebook.com/europacific.llc/?locale=sl_SI" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#003366]" title="Facebook"><Facebook className="h-4 w-4" /></a>
              <a href="https://www.linkedin.com/company/europacific-d-o-o-/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#003366]" title="LinkedIn"><Linkedin className="h-4 w-4" /></a>
              <a href="https://www.tiktok.com/@europacific" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#003366]" title="TikTok"><Music2 className="h-4 w-4" /></a>
            </div>
            <p className="text-gray-500 text-xs">© 2026 Europacific d.o.o. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
